import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, Newspaper } from 'lucide-react';
import api from '../../lib/api';

export const RssFeedSlider = ({ feedUrl, title = "News & Articoli" }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!feedUrl) {
      setLoading(false);
      return;
    }

    const loadFeed = async () => {
      try {
        const res = await api.get(`/rss/parse?url=${encodeURIComponent(feedUrl)}`);
        if (res.data.articles && res.data.articles.length > 0) {
          setArticles(res.data.articles);
        } else if (res.data.error) {
          setError(res.data.error);
        }
      } catch (err) {
        console.error('Error loading RSS feed:', err);
        setError('Impossibile caricare gli articoli');
      } finally {
        setLoading(false);
      }
    };

    loadFeed();
  }, [feedUrl]);

  // Items per page based on screen size
  const getItemsPerPage = () => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768 ? 4 : 2;
    }
    return 4;
  };

  const [itemsPerPage, setItemsPerPage] = useState(getItemsPerPage());

  useEffect(() => {
    const handleResize = () => {
      setItemsPerPage(getItemsPerPage());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxIndex = Math.max(0, articles.length - itemsPerPage);

  const nextSlide = () => {
    setCurrentIndex(prev => Math.min(prev + 1, maxIndex));
  };

  const prevSlide = () => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  };

  if (!feedUrl) return null;

  if (loading) {
    return (
      <section className="py-16 bg-black/20" data-testid="rss-feed-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Newspaper className="text-primary animate-pulse" size={24} />
            <span className="text-muted-foreground">Caricamento articoli...</span>
          </div>
        </div>
      </section>
    );
  }

  if (error || articles.length === 0) {
    return null; // Don't show section if no articles
  }

  const visibleArticles = articles.slice(currentIndex, currentIndex + itemsPerPage);

  return (
    <section className="py-16 bg-black/20" data-testid="rss-feed-section">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
              <Newspaper className="text-primary" size={20} />
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold">{title}</h2>
          </div>

          {/* Navigation Arrows */}
          {articles.length > itemsPerPage && (
            <div className="flex gap-2">
              <button
                onClick={prevSlide}
                disabled={currentIndex === 0}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  currentIndex === 0 
                    ? 'bg-white/5 text-white/30 cursor-not-allowed' 
                    : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
                aria-label="Articoli precedenti"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={nextSlide}
                disabled={currentIndex >= maxIndex}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  currentIndex >= maxIndex 
                    ? 'bg-white/5 text-white/30 cursor-not-allowed' 
                    : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
                aria-label="Articoli successivi"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>

        {/* Articles Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {visibleArticles.map((article, index) => (
            <a
              key={index}
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group glass rounded-xl overflow-hidden hover:bg-white/10 transition-all duration-300 flex flex-col"
              data-testid={`rss-article-${index}`}
            >
              {/* Image */}
              <div className="aspect-video relative overflow-hidden bg-white/5">
                {article.image ? (
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const fallback = e.target.parentElement?.querySelector('[data-fallback]');
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  data-fallback
                  className={`absolute inset-0 ${article.image ? 'hidden' : 'flex'} items-center justify-center bg-gradient-to-br from-primary/20 to-violet-900/20`}
                >
                  <Newspaper size={32} className="text-white/30" />
                </div>
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <ExternalLink size={24} className="text-white" />
                </div>
              </div>

              {/* Content */}
              <div className="p-4 flex-1 flex flex-col">
                <h3 className="font-semibold text-sm md:text-base line-clamp-2 mb-2 group-hover:text-primary transition-colors">
                  {article.title}
                </h3>
                {article.subtitle && (
                  <p className="text-xs md:text-sm text-muted-foreground line-clamp-2 flex-1">
                    {article.subtitle}
                  </p>
                )}
              </div>
            </a>
          ))}
        </div>

        {/* Pagination dots */}
        {articles.length > itemsPerPage && (
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: Math.ceil(articles.length / itemsPerPage) }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i * itemsPerPage)}
                className={`w-2 h-2 rounded-full transition-all ${
                  Math.floor(currentIndex / itemsPerPage) === i
                    ? 'bg-primary w-6'
                    : 'bg-white/30 hover:bg-white/50'
                }`}
                aria-label={`Vai a pagina ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default RssFeedSlider;
